import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2, X } from 'lucide-react';
import { api } from '@/api/client';

type Perm = { module: string; tab: string; view: boolean; add: boolean; edit: boolean; delete: boolean; export: boolean };
type Role = { id: number; name: string; description?: string; isActive: boolean; permissions: Perm[] };
type ModuleTabs = { module: string; tabs: string[] };

export function RolesPage() {
  const qc = useQueryClient();
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState<Role | null>(null);

  const { data: roles = [], isLoading } = useQuery({
    queryKey: ['roles'],
    queryFn: async () => (await api.get<Role[]>('/roles')).data,
  });

  const del = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/roles/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['roles'] }),
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Failed'),
  });

  const grantedTabs = (r: Role) => (r.permissions ?? []).filter((p) => p.view || p.add || p.edit || p.delete || p.export).length;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Roles &amp; Permissions</h1>
        <button onClick={() => { setEditing(null); setShow(true); }} className="px-4 py-2 border border-emerald-600 text-emerald-700 rounded text-sm flex items-center gap-1">
          <Plus size={14} /> Add Role
        </button>
      </div>
      <div className="overflow-x-auto bg-white text-slate-900 border rounded">
        <table className="min-w-full text-sm">
          <thead className="bg-emerald-50">
            <tr>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Description</th>
              <th className="px-3 py-2 text-right">Tabs granted</th>
              <th className="px-3 py-2 text-center">Status</th>
              <th className="px-3 py-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={5} className="text-center py-6 text-slate-400">Loading…</td></tr>
            ) : (roles as Role[]).length === 0 ? (
              <tr><td colSpan={5} className="text-center py-6 text-slate-400">No roles yet</td></tr>
            ) : (
              (roles as Role[]).map((r) => (
                <tr key={r.id} className="border-t">
                  <td className="px-3 py-2 font-medium">{r.name}</td>
                  <td className="px-3 py-2 text-slate-500">{r.description ?? '—'}</td>
                  <td className="px-3 py-2 text-right">{grantedTabs(r)}</td>
                  <td className="px-3 py-2 text-center">{r.isActive ? <span className="text-emerald-600">●</span> : <span className="text-slate-400">●</span>}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1">
                      <button onClick={() => { setEditing(r); setShow(true); }} className="border rounded px-2 py-1 text-xs flex items-center gap-1"><Pencil size={12} /> Edit</button>
                      <button onClick={() => confirm(`Delete role "${r.name}"?`) && del.mutate(r.id)} className="border border-red-300 rounded px-2 py-1 text-xs text-red-600"><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {show && <RoleForm initial={editing} onClose={() => { setShow(false); setEditing(null); }} onSaved={() => { setShow(false); setEditing(null); qc.invalidateQueries({ queryKey: ['roles'] }); }} />}
    </div>
  );
}

function RoleForm({ initial, onClose, onSaved }: any) {
  const { data: meta } = useQuery({
    queryKey: ['roles-modules'],
    queryFn: async () => (await api.get<{ moduleTabs: ModuleTabs[]; modules: string[]; actions: string[] }>('/roles/modules')).data,
  });
  const moduleTabs: ModuleTabs[] = meta?.moduleTabs ?? [];
  const actions = meta?.actions ?? ['view', 'add', 'edit', 'delete', 'export'];

  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);
  const [perms, setPerms] = useState<Perm[]>([]);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  // Build the module×tab grid from the catalog, overlaying any existing role permissions.
  // Legacy entries (module-level, no tab) are applied to every tab of that module.
  useEffect(() => {
    if (!moduleTabs.length) return;
    const existing: Perm[] = initial?.permissions ?? [];
    const findExisting = (module: string, tab: string) => {
      const exact = existing.find((p: any) => p.module === module && p.tab === tab);
      if (exact) return exact;
      return existing.find((p: any) => p.module === module && (p.tab === undefined || p.tab === null || p.tab === 'All'));
    };
    const built: Perm[] = [];
    for (const m of moduleTabs) {
      for (const tab of m.tabs) {
        const e: any = findExisting(m.module, tab) ?? {};
        built.push({ module: m.module, tab, view: !!e.view, add: !!e.add, edit: !!e.edit, delete: !!e.delete, export: !!e.export });
      }
    }
    setPerms(built);
    // collapse all modules except HR + Warehouses by default (the tab-heavy ones)
    const c: Record<string, boolean> = {};
    for (const m of moduleTabs) c[m.module] = !(m.module === 'HR' || m.module === 'Warehouses');
    setCollapsed(c);
  }, [meta, initial]);

  const setCell = (module: string, tab: string, action: keyof Perm, v: boolean) =>
    setPerms((ps) => ps.map((p) => (p.module === module && p.tab === tab ? { ...p, [action]: v } : p)));
  const setTabAll = (module: string, tab: string, v: boolean) =>
    setPerms((ps) => ps.map((p) => (p.module === module && p.tab === tab ? { ...p, view: v, add: v, edit: v, delete: v, export: v } : p)));
  const setModuleAll = (module: string, v: boolean) =>
    setPerms((ps) => ps.map((p) => (p.module === module ? { ...p, view: v, add: v, edit: v, delete: v, export: v } : p)));

  const save = useMutation({
    mutationFn: async () => {
      const body = { name, description, isActive, permissions: perms };
      if (initial) return (await api.patch(`/roles/${initial.id}`, body)).data;
      return (await api.post('/roles', body)).data;
    },
    onSuccess: onSaved,
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Failed to save role'),
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-12 z-50">
      <div className="bg-white text-slate-900 rounded-lg w-[960px] max-h-[88vh] overflow-y-auto">
        <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between rounded-t">
          <div className="font-semibold">{initial ? 'Edit Role' : 'Add Role'}</div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Role Name *</label>
              <input value={name} onChange={(e) => setName(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-slate-500 mb-1">Description</label>
              <input value={description} onChange={(e) => setDescription(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" />
            </div>
          </div>
          <div className="text-xs text-slate-500">Grant access per <b>module → tab</b>. HR and Warehouses have detailed tabs; other modules have a single “All” tab. (SUPER_ADMIN and ADMIN always have full access.)</div>
          <div className="border rounded divide-y">
            {moduleTabs.map((m) => {
              const rows = perms.filter((p) => p.module === m.module);
              const isCollapsed = collapsed[m.module];
              const anyGranted = rows.some((p) => p.view || p.add || p.edit || p.delete || p.export);
              return (
                <div key={m.module}>
                  <div className="flex items-center justify-between bg-emerald-50 px-3 py-2">
                    <button type="button" onClick={() => setCollapsed((c) => ({ ...c, [m.module]: !c[m.module] }))} className="font-semibold text-sm text-gray-900 flex items-center gap-2">
                      <span className="text-emerald-700">{isCollapsed ? '▸' : '▾'}</span> <span className="text-gray-900">{m.module}</span>
                      {anyGranted && <span className="text-[10px] bg-emerald-600 text-white rounded px-1.5 py-0.5">granted</span>}
                      <span className="text-xs text-gray-600">({m.tabs.length} tab{m.tabs.length > 1 ? 's' : ''})</span>
                    </button>
                    <div className="flex gap-2 text-xs">
                      <button type="button" onClick={() => setModuleAll(m.module, true)} className="text-emerald-700 underline">grant all</button>
                      <button type="button" onClick={() => setModuleAll(m.module, false)} className="text-gray-500 underline">clear</button>
                    </div>
                  </div>
                  {!isCollapsed && (
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-3 py-1.5 text-left w-56">Tab</th>
                          {actions.map((a) => <th key={a} className="px-2 py-1.5 text-center capitalize">{a}</th>)}
                          <th className="px-2 py-1.5 text-center">All</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((p) => (
                          <tr key={p.tab} className="border-t">
                            <td className="px-3 py-1.5">{p.tab}</td>
                            {actions.map((a) => (
                              <td key={a} className="px-2 py-1.5 text-center">
                                <input type="checkbox" checked={!!(p as any)[a]} onChange={(e) => setCell(p.module, p.tab, a as any, e.target.checked)} />
                              </td>
                            ))}
                            <td className="px-2 py-1.5 text-center">
                              <button type="button" onClick={() => setTabAll(p.module, p.tab, !(p.view && p.add && p.edit && p.delete && p.export))} className="text-xs text-emerald-700 underline">toggle</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}
          </div>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            <span className="text-sm">Active</span>
          </label>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-3">
          <button onClick={onClose} className="border border-red-400 text-red-600 px-4 py-1.5 rounded text-sm">CANCEL</button>
          <button onClick={() => save.mutate()} disabled={save.isPending || !name} className="bg-emerald-600 text-white px-5 py-1.5 rounded text-sm disabled:opacity-50">{save.isPending ? 'Saving…' : 'Save Role'}</button>
        </div>
      </div>
    </div>
  );
}
